import type { GithubRepo, ImportPlan } from "@codvero/shared";
import { useMutation, useQuery } from "@tanstack/react-query";
import { AlertTriangle, FileArchive, Folder, FolderUp, Github, Lock } from "lucide-react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { githubApi, importsApi } from "@/api/imports.ts";
import { cn } from "@/lib/cn.ts";
import { Alert, Button, Field, Input, Select, Spinner } from "@/ui/primitives.tsx";
import { useApiError } from "@/ui/useApiError.ts";

export type ImportSource = "upload" | "github";

function fmt(b: number) {
  return b >= 1 << 20 ? `${(b / (1 << 20)).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`;
}

/**
 * Import into a running project: from the computer (folder or ZIP → scan → plan → approval → apply)
 * or from GitHub (URL or repo picker → clone inside the container).
 */
export function ImportWizard({ projectId, source, onDone, onCancel }: { projectId: string; source: ImportSource; onDone: (summary: string) => void; onCancel?: () => void }) {
  return source === "upload" ? <UploadFlow projectId={projectId} onDone={onDone} onCancel={onCancel} /> : <GithubFlow projectId={projectId} onDone={onDone} onCancel={onCancel} />;
}

function UploadFlow({ projectId, onDone, onCancel }: { projectId: string; onDone: (s: string) => void; onCancel?: (() => void) | undefined }) {
  const { t } = useTranslation();
  const toMsg = useApiError();
  const folderRef = useRef<HTMLInputElement>(null);
  const zipRef = useRef<HTMLInputElement>(null);
  const [plan, setPlan] = useState<ImportPlan | null>(null);
  const [include, setInclude] = useState<Set<string>>(new Set());
  const [mode, setMode] = useState<"merge" | "replace">("merge");
  const [progress, setProgress] = useState<string | null>(null);
  const stage = useMutation({
    mutationFn: async (input: { kind: "folder" | "zip"; files: FileList }) => {
      if (input.kind === "zip") return importsApi.stageZip(projectId, input.files[0]!);
      const list = Array.from(input.files).map((file) => ({ file, path: (file as File & { webkitRelativePath?: string }).webkitRelativePath || file.name }));
      setProgress(t("import.uploading", { n: list.length }));
      return importsApi.stageFiles(projectId, list);
    },
    onSuccess: (p) => { setPlan(p); setInclude(new Set()); setMode(p.workspaceHasFiles ? "merge" : "merge"); setProgress(null); },
    onError: () => setProgress(null),
  });
  const apply = useMutation({
    mutationFn: () => importsApi.apply(projectId, plan!.id, { mode, include: [...include] }),
    onSuccess: (r) => onDone(t("import.done", { n: r.files })),
  });
  const discard = () => { if (plan) void importsApi.discard(projectId, plan.id).catch(() => undefined); setPlan(null); };

  if (!plan) {
    return (
      <div className="space-y-3">
        <p className="text-sm text-fg-muted">{t("import.pickHint")}</p>
        <input ref={(el) => { folderRef.current = el; el?.setAttribute("webkitdirectory", ""); }} type="file" multiple className="hidden" onChange={(e) => { if (e.target.files?.length) stage.mutate({ kind: "folder", files: e.target.files }); e.target.value = ""; }} />
        <input ref={zipRef} type="file" accept=".zip,application/zip" className="hidden" onChange={(e) => { if (e.target.files?.length) stage.mutate({ kind: "zip", files: e.target.files }); e.target.value = ""; }} />
        <div className="grid gap-2 sm:grid-cols-2">
          <button type="button" disabled={stage.isPending} onClick={() => folderRef.current?.click()} className="flex items-start gap-3 rounded-md border border-border p-3 text-start hover:border-accent disabled:opacity-50">
            <FolderUp className="mt-0.5 size-4 shrink-0" />
            <span><span className="block text-sm font-medium">{t("import.folder")}</span><span className="block text-xs text-fg-muted">{t("import.folderDesc")}</span></span>
          </button>
          <button type="button" disabled={stage.isPending} onClick={() => zipRef.current?.click()} className="flex items-start gap-3 rounded-md border border-border p-3 text-start hover:border-accent disabled:opacity-50">
            <FileArchive className="mt-0.5 size-4 shrink-0" />
            <span><span className="block text-sm font-medium">{t("import.zip")}</span><span className="block text-xs text-fg-muted">{t("import.zipDesc")}</span></span>
          </button>
        </div>
        {(stage.isPending || progress) && <div className="flex items-center gap-2 text-sm text-fg-muted"><Spinner className="size-4" /> {progress ?? t("import.scanning")}</div>}
        {stage.error && <Alert tone="danger">{toMsg(stage.error)}</Alert>}
        {onCancel && <div className="flex justify-end"><Button type="button" variant="ghost" onClick={onCancel}>{t("common.cancel")}</Button></div>}
      </div>
    );
  }

  const toggle = (p: string) => setInclude((s) => { const n = new Set(s); if (n.has(p)) n.delete(p); else n.add(p); return n; });
  return (
    <div className="space-y-3">
      <div className="rounded-md border border-border bg-surface-2/40 p-3 text-sm">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
          <span><strong>{plan.fileCount}</strong> {t("import.files")}</span>
          <span>{fmt(plan.totalBytes)}</span>
          {plan.root && <span className="text-fg-muted" dir="ltr">{plan.root}/ → /workspace</span>}
          {plan.stack.length > 0 && <span className="flex flex-wrap gap-1">{plan.stack.map((s) => <span key={s} className="rounded bg-accent-soft px-1.5 text-[11px] text-accent">{s}</span>)}</span>}
        </div>
        <ul className="mt-2 grid max-h-32 grid-cols-2 gap-x-4 overflow-y-auto font-mono text-xs sm:grid-cols-3" dir="ltr">
          {plan.topLevel.map((e) => <li key={e.name} className="flex items-center gap-1 truncate"><Folder className={cn("size-3 shrink-0", e.dir ? "text-accent" : "invisible")} />{e.name}{e.dir ? "/" : ""}<span className="ms-auto text-fg-muted">{fmt(e.bytes)}</span></li>)}
        </ul>
      </div>
      {plan.warnings.map((w) => <Alert key={w} tone="warn"><AlertTriangle className="me-1 inline size-3.5" /> {w}</Alert>)}
      {plan.skipped.length > 0 && (
        <details open={plan.skipped.length <= 8} className="rounded-md border border-border p-3 text-sm">
          <summary className="cursor-pointer">{t("import.skipped", { n: plan.skipped.length })} <span className="text-xs text-fg-muted">— {t("import.skippedHint")}</span></summary>
          <ul className="mt-2 max-h-40 space-y-1 overflow-y-auto">
            {plan.skipped.map((s) => (
              <li key={s.path} className="flex items-center gap-2 text-xs">
                <input type="checkbox" className="accent-accent" checked={include.has(s.path)} onChange={() => toggle(s.path)} />
                <span dir="ltr" className="min-w-0 flex-1 truncate font-mono">{s.path}</span>
                <span className={cn("rounded px-1.5 text-[10px]", s.reason === "secret" ? "bg-danger-soft text-danger" : "bg-surface-2 text-fg-muted")}>{s.reason === "secret" && <Lock className="me-0.5 inline size-2.5" />}{t(`import.reason.${s.reason}`)}</span>
                <span className="w-14 text-end text-fg-muted">{fmt(s.bytes)}</span>
              </li>
            ))}
          </ul>
        </details>
      )}
      {plan.workspaceHasFiles && (
        <Field label={t("import.mode")}>
          <Select value={mode} onChange={(e) => setMode(e.target.value as "merge" | "replace")}>
            <option value="merge">{t("import.merge")}</option>
            <option value="replace">{t("import.replace")}</option>
          </Select>
        </Field>
      )}
      {apply.error && <Alert tone="danger">{toMsg(apply.error)}</Alert>}
      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" onClick={discard}>{t("import.back")}</Button>
        <Button type="button" loading={apply.isPending} disabled={plan.fileCount + include.size === 0} onClick={() => apply.mutate()}>{t("import.apply")}</Button>
      </div>
    </div>
  );
}

function GithubFlow({ projectId, onDone, onCancel }: { projectId: string; onDone: (s: string) => void; onCancel?: (() => void) | undefined }) {
  const { t } = useTranslation();
  const toMsg = useApiError();
  const status = useQuery({ queryKey: ["github", "status"], queryFn: githubApi.status });
  const [q, setQ] = useState("");
  const repos = useQuery({ queryKey: ["github", "repos"], queryFn: () => githubApi.repos(), enabled: !!status.data?.connected, staleTime: 60_000 });
  const [url, setUrl] = useState("");
  const [branch, setBranch] = useState("");
  const [mode, setMode] = useState<"merge" | "replace">("merge");
  const clone = useMutation({
    mutationFn: () => importsApi.clone(projectId, { url, mode, ...(branch ? { branch } : {}) }),
    onSuccess: () => onDone(t("import.cloned")),
  });
  const pick = (r: GithubRepo) => { setUrl(r.cloneUrl); setBranch(r.defaultBranch); };
  const shown = (repos.data ?? []).filter((r) => !q || r.fullName.toLowerCase().includes(q.toLowerCase())).slice(0, 30);
  return (
    <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); if (url.trim()) clone.mutate(); }}>
      {status.data && !status.data.connected && (
        <Alert tone="info" className="flex flex-wrap items-center gap-2">
          <Github className="size-4" />
          <span className="flex-1">{status.data.configured ? t("import.ghConnectHint") : t("import.ghNotConfigured")}</span>
          {status.data.configured && <a className="rounded-md border border-border px-2 py-1 text-xs hover:bg-surface-2" href={githubApi.startUrl}>{t("import.ghConnect")}</a>}
        </Alert>
      )}
      {status.data?.connected && (
        <div className="space-y-2">
          <Input placeholder={t("import.ghSearch", { login: status.data.login })} value={q} onChange={(e) => setQ(e.target.value)} />
          <ul className="max-h-40 divide-y divide-border overflow-y-auto rounded-md border border-border text-sm">
            {repos.isPending && <li className="p-2"><Spinner className="size-4" /></li>}
            {shown.map((r) => (
              <li key={r.fullName}>
                <button type="button" onClick={() => pick(r)} className={cn("flex w-full items-center gap-2 px-3 py-1.5 text-start hover:bg-surface-2", url === r.cloneUrl && "bg-accent-soft/50")}>
                  {r.private ? <Lock className="size-3 text-fg-muted" /> : <Github className="size-3 text-fg-muted" />}
                  <span dir="ltr" className="min-w-0 flex-1 truncate font-mono text-xs">{r.fullName}</span>
                  <span className="text-[10px] text-fg-muted">{r.defaultBranch}</span>
                </button>
              </li>
            ))}
            {!repos.isPending && shown.length === 0 && <li className="p-2 text-xs text-fg-muted">—</li>}
          </ul>
        </div>
      )}
      <Field label={t("import.repoUrl")} hint={t("import.repoUrlHint")}>
        <Input dir="ltr" required placeholder="https://github.com/owner/repo" value={url} onChange={(e) => setUrl(e.target.value)} />
      </Field>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label={t("import.branch")}><Input dir="ltr" placeholder="main" value={branch} onChange={(e) => setBranch(e.target.value)} /></Field>
        <Field label={t("import.mode")}>
          <Select value={mode} onChange={(e) => setMode(e.target.value as "merge" | "replace")}>
            <option value="merge">{t("import.mergeEmpty")}</option>
            <option value="replace">{t("import.replace")}</option>
          </Select>
        </Field>
      </div>
      {clone.error && <Alert tone="danger"><pre className="whitespace-pre-wrap font-mono text-xs">{toMsg(clone.error)}</pre></Alert>}
      <div className="flex justify-end gap-2">
        {onCancel && <Button type="button" variant="ghost" onClick={onCancel}>{t("common.cancel")}</Button>}
        <Button type="submit" loading={clone.isPending} disabled={!url.trim()}>{clone.isPending ? t("import.cloning") : t("import.clone")}</Button>
      </div>
    </form>
  );
}
